/**
 * Template Engine
 *
 * Generates HVAC system nodes, edges, and plant loops from templates.
 * Handles conditional logic, parameter substitution, and zone equipment creation.
 */

import type { XYPosition } from 'reactflow';
import type { PlantLoopType } from '../plant/types';
import type {
  HVACTemplate,
  ZoneSelection,
  GeneratedSystem,
  TemplateApplicationResult,
} from './types';
import { getComponentDefinition } from '../hvac/components';
import { useHVACStore } from '../hvac/hvacStore';
import { usePlantLoopStore } from '../plant/plantStore';

/**
 * Generate unique ID
 */
function generateId(prefix: string): string {
  return `${prefix}_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Evaluate conditional expression with parameters
 */
function evaluateCondition(
  condition: string | undefined,
  parameters: Record<string, string | number | boolean>
): boolean {
  if (!condition) return true;

  try {
    // Simple expression parser for common patterns
    // Supports: parameters.x === 'value', parameters.x === true/false, parameters.x !== 'value'

    // Replace parameter references
    const expr = condition.replace(/parameters\.(\w+)/g, (_, key: string) => {
      const value = parameters[key];
      if (typeof value === 'string') return `"${value}"`;
      if (typeof value === 'boolean') return value ? 'true' : 'false';
      if (typeof value === 'number') return String(value);
      return 'undefined';
    });

    // Evaluate the expression
    // eslint-disable-next-line no-new-func
    const fn = new Function(`return ${expr};`);
    return Boolean(fn());
  } catch (e) {
    console.warn(`Failed to evaluate condition: ${condition}`, e);
    return false;
  }
}

/**
 * Substitute parameters in a name pattern
 */
function substituteNamePattern(
  pattern: string,
  systemName: string,
  parameters: Record<string, string | number | boolean>,
  zoneName?: string
): string {
  let result = pattern.replace('{SystemName}', systemName);
  result = result.replace('{ZoneName}', zoneName || '');

  // Replace any parameter references
  for (const [key, value] of Object.entries(parameters)) {
    result = result.replace(`{${key}}`, String(value));
  }

  return result;
}

/**
 * Generate a preview of the system without applying it
 */
export function generateSystemPreview(
  template: HVACTemplate,
  systemName: string,
  parameters: Record<string, string | number | boolean>,
  selectedZones: ZoneSelection[]
): GeneratedSystem {
  const nodes: GeneratedSystem['nodes'] = [];
  const edges: GeneratedSystem['edges'] = [];
  const plantLoops: GeneratedSystem['plantLoops'] = [];

  // Track which components are included (after conditional evaluation)
  const includedComponents = new Set<string>();
  const componentIdMap = new Map<string, string>(); // template component id -> generated node id

  // Generate air-side components
  for (const component of template.components) {
    // Evaluate conditional
    if (!evaluateCondition(component.conditional, parameters)) {
      continue;
    }

    includedComponents.add(component.id);
    const nodeId = generateId('node');
    componentIdMap.set(component.id, nodeId);

    const componentDef = getComponentDefinition(component.componentType);
    if (!componentDef) {
      console.warn(`Unknown component type: ${component.componentType}`);
      continue;
    }

    const name = substituteNamePattern(component.namePattern, systemName, parameters);

    nodes.push({
      id: nodeId,
      componentType: component.componentType,
      name,
      position: component.position,
      fields: {
        ...componentDef.defaults,
        ...component.fields,
      },
    });
  }

  // Generate connections between included components
  for (const connection of template.connections) {
    // Skip if either component was not included
    if (!includedComponents.has(connection.sourceId) || !includedComponents.has(connection.targetId)) {
      continue;
    }

    // Evaluate conditional
    if (!evaluateCondition(connection.conditional, parameters)) {
      continue;
    }

    const sourceNodeId = componentIdMap.get(connection.sourceId);
    const targetNodeId = componentIdMap.get(connection.targetId);

    if (sourceNodeId && targetNodeId) {
      edges.push({
        sourceId: sourceNodeId,
        sourcePort: connection.sourcePort,
        targetId: targetNodeId,
        targetPort: connection.targetPort,
      });
    }
  }

  // Generate zone equipment for each selected zone
  if (template.requiresZones && selectedZones.length > 0 && template.terminalTypes && template.terminalTypes.length > 0) {
    // Find the splitter and mixer in generated components
    const splitterNode = nodes.find((n) => n.componentType === 'supply-splitter');
    const mixerNode = nodes.find((n) => n.componentType === 'return-mixer');

    let zoneIndex = 0;
    for (const zone of selectedZones) {
      if (!zone.selected) continue;

      // Get terminal type for this zone
      const terminalType = zone.terminalType || template.terminalTypes[0]?.componentType;
      if (!terminalType) continue;

      const terminalDef = getComponentDefinition(terminalType);
      if (!terminalDef) continue;

      const terminalConfig = template.terminalTypes.find((t) => t.componentType === terminalType);

      // Create terminal unit for zone
      const terminalId = generateId('terminal');
      const terminalName = `${zone.zoneName} ${terminalDef.displayName}`;

      // Position terminals in a column to the right of the splitter
      const baseX = splitterNode ? splitterNode.position.x + 200 : 1000;
      const baseY = 100 + zoneIndex * 120;

      nodes.push({
        id: terminalId,
        componentType: terminalType,
        name: terminalName,
        position: { x: baseX, y: baseY },
        fields: {
          ...terminalDef.defaults,
          ...terminalConfig?.fields,
        },
      });

      // Connect splitter to terminal (if splitter exists)
      if (splitterNode) {
        const outletPort = `outlet-${String(Math.min(zoneIndex + 1, 4))}`;
        edges.push({
          sourceId: splitterNode.id,
          sourcePort: outletPort,
          targetId: terminalId,
          targetPort: 'air-inlet',
        });
      }

      // Connect terminal to mixer (if mixer exists)
      if (mixerNode) {
        const inletPort = `inlet-${String(Math.min(zoneIndex + 1, 4))}`;
        edges.push({
          sourceId: terminalId,
          sourcePort: 'air-outlet',
          targetId: mixerNode.id,
          targetPort: inletPort,
        });
      }

      zoneIndex++;
    }
  }

  // Generate plant loops
  if (template.plantLoops) {
    for (const loopTemplate of template.plantLoops) {
      if (!evaluateCondition(loopTemplate.conditional, parameters)) {
        continue;
      }

      const loopName = loopTemplate.namePattern
        ? substituteNamePattern(loopTemplate.namePattern, systemName, parameters)
        : `${systemName} ${getLoopTypeName(loopTemplate.type)}`;

      plantLoops.push({
        type: loopTemplate.type,
        name: loopName,
        equipment: loopTemplate.equipment,
      });
    }
  }

  return { nodes, edges, plantLoops };
}

/**
 * Get display name for loop type
 */
function getLoopTypeName(type: PlantLoopType): string {
  const names: Record<PlantLoopType, string> = {
    'chilled-water': 'Chilled Water Loop',
    'hot-water': 'Hot Water Loop',
    'condenser-water': 'Condenser Water Loop',
    'ground-loop': 'Ground Loop',
    'service-hot-water': 'Service Hot Water Loop',
  };
  return names[type] || type;
}

/**
 * Apply a template to create actual nodes and edges
 */
export function applyTemplate(
  template: HVACTemplate,
  systemName: string,
  parameters: Record<string, string | number | boolean>,
  selectedZones: ZoneSelection[],
  basePosition: XYPosition = { x: 100, y: 100 }
): TemplateApplicationResult {
  const hvacStore = useHVACStore.getState();
  const plantStore = usePlantLoopStore.getState();

  const createdNodeIds: string[] = [];
  const createdEdgeIds: string[] = [];
  const createdPlantLoopIds: string[] = [];

  try {
    // Generate the system preview first
    const preview = generateSystemPreview(template, systemName, parameters, selectedZones);

    // Create a map from preview node IDs to actual node IDs
    const nodeIdMap = new Map<string, string>();

    // Create nodes
    for (const nodeData of preview.nodes) {
      const actualPosition: XYPosition = {
        x: basePosition.x + nodeData.position.x,
        y: basePosition.y + nodeData.position.y,
      };

      const nodeId = hvacStore.addNode(
        nodeData.componentType,
        actualPosition,
        nodeData.name
      );

      if (nodeId) {
        createdNodeIds.push(nodeId);
        nodeIdMap.set(nodeData.id, nodeId);

        // Update node fields
        hvacStore.updateNodeData(nodeId, { fields: nodeData.fields });
      }
    }

    // Create edges
    for (const edgeData of preview.edges) {
      const sourceId = nodeIdMap.get(edgeData.sourceId);
      const targetId = nodeIdMap.get(edgeData.targetId);

      if (sourceId && targetId) {
        const edgeId = hvacStore.addEdge({
          source: sourceId,
          target: targetId,
          sourceHandle: edgeData.sourcePort,
          targetHandle: edgeData.targetPort,
        });

        if (edgeId) {
          createdEdgeIds.push(edgeId);
        }
      }
    }

    // Create plant loops
    for (const loopData of preview.plantLoops) {
      const loopId = plantStore.createLoop(loopData.type, loopData.name);

      if (loopId) {
        createdPlantLoopIds.push(loopId);

        // Add primary equipment to the loop
        // This would need more sophisticated logic based on equipment type
        // For now, we just create the loop
      }
    }

    return {
      success: true,
      nodeIds: createdNodeIds,
      edgeIds: createdEdgeIds,
      plantLoopIds: createdPlantLoopIds,
      undoData: {
        nodeIds: createdNodeIds,
        edgeIds: createdEdgeIds,
        plantLoopIds: createdPlantLoopIds,
      },
    };
  } catch (error) {
    // Rollback on error
    for (const nodeId of createdNodeIds) {
      hvacStore.removeNode(nodeId);
    }
    for (const edgeId of createdEdgeIds) {
      hvacStore.removeEdge(edgeId);
    }
    for (const loopId of createdPlantLoopIds) {
      plantStore.deleteLoop(loopId);
    }

    return {
      success: false,
      nodeIds: [],
      edgeIds: [],
      plantLoopIds: [],
      error: error instanceof Error ? error.message : 'Unknown error',
      undoData: {
        nodeIds: [],
        edgeIds: [],
        plantLoopIds: [],
      },
    };
  }
}

/**
 * Undo a template application
 */
export function undoTemplateApplication(result: TemplateApplicationResult): void {
  const hvacStore = useHVACStore.getState();
  const plantStore = usePlantLoopStore.getState();

  // Remove in reverse order
  for (const loopId of result.plantLoopIds) {
    plantStore.deleteLoop(loopId);
  }

  for (const edgeId of result.edgeIds) {
    hvacStore.removeEdge(edgeId);
  }

  for (const nodeId of result.nodeIds) {
    hvacStore.removeNode(nodeId);
  }
}

/**
 * Calculate optimal layout positions for generated nodes
 */
export function calculateLayoutPositions(
  nodes: GeneratedSystem['nodes'],
  _edges: GeneratedSystem['edges']
): GeneratedSystem['nodes'] {
  // Group nodes by their approximate x position (for layering)
  const layers = new Map<number, typeof nodes>();

  for (const node of nodes) {
    const layerKey = Math.round(node.position.x / 150) * 150;
    if (!layers.has(layerKey)) {
      layers.set(layerKey, []);
    }
    layers.get(layerKey)?.push(node);
  }

  // Spread nodes vertically within each layer
  const result: typeof nodes = [];
  for (const [layerX, layerNodes] of layers) {
    const layerHeight = layerNodes.length * 100;
    const startY = -layerHeight / 2 + 200;

    layerNodes.forEach((node, index) => {
      result.push({
        ...node,
        position: {
          x: layerX,
          y: startY + index * 100,
        },
      });
    });
  }

  return result;
}

/**
 * Get zones from IDF document
 */
export function getZonesFromIDF(idfDocument: { objects: Array<{ className: string; fields: Array<{ value: string }>; id: string }> } | null): Array<{ name: string; id: string }> {
  if (!idfDocument) return [];

  const zones: Array<{ name: string; id: string }> = [];

  for (const obj of idfDocument.objects) {
    if (obj.className.toLowerCase() === 'zone') {
      const nameField = obj.fields[0];
      if (nameField) {
        zones.push({
          name: nameField.value,
          id: obj.id,
        });
      }
    }
  }

  return zones;
}
